import type { CurrencyCode } from '@klear/constants/currency'
import { KlearDate } from '@klear/date'
import type { KlearDateJson } from '@klear/date/json'
import { Money } from '@klear/money'
import {
  IN_TRANSIT_PAYMENT_STATUSES,
  normalizePaymentStatus,
  SETTLED_PAYMENT_STATUSES,
  TERMINAL_FAILURE_PAYMENT_STATUSES
} from '@klear/payment/constants'
import type { Payment } from '@klear/payment/payment'
import { paymentIsDraft } from '@klear/payment/status'
import {
  PAAS_ADVANCE_PAYMENT_TYPES,
  PAAS_ALLOCATION_ROLES,
  PAAS_FEE_PAYMENT_TYPES,
  PAAS_REVERSAL_PAYMENT_STATUSES,
  PAAS_SETTLEMENT_CURRENCY,
  PAAS_STATUS_COLLECTED,
  PAAS_STATUS_DRAFT,
  PAAS_STATUS_OUTSTANDING,
  PAAS_STATUS_PARTIALLY_COLLECTED,
  PAAS_STATUS_PENDING,
  PAAS_STATUS_RECONCILE,
  PAAS_STATUS_VOIDED,
  type PaasStatus
} from '@klear/paas/constants'

type PaymentSettlementFields = Pick<Payment, 'status'>

export function paasPaymentIsSettled(payment: PaymentSettlementFields): boolean {
  const status = normalizePaymentStatus(payment.status)
  return SETTLED_PAYMENT_STATUSES.some((settled) => settled === status)
}

export function paasPaymentIsInTransit(payment: PaymentSettlementFields): boolean {
  const status = normalizePaymentStatus(payment.status)
  return IN_TRANSIT_PAYMENT_STATUSES.some((inTransit) => inTransit === status)
}

export function paasPaymentIsVoided(payment: PaymentSettlementFields): boolean {
  const status = normalizePaymentStatus(payment.status)
  return TERMINAL_FAILURE_PAYMENT_STATUSES.some((voided) => voided === status)
}

export function paasPaymentIsReversal(payment: PaymentSettlementFields): boolean {
  const status = normalizePaymentStatus(payment.status)
  return PAAS_REVERSAL_PAYMENT_STATUSES.some((reversed) => reversed === status)
}

export type PaasAllocationRole = (typeof PAAS_ALLOCATION_ROLES)[number]

export function paasIsFeePayment(payment: Pick<Payment, 'paymentType'>): boolean {
  return PAAS_FEE_PAYMENT_TYPES.some((type) => type === payment.paymentType)
}

export function paasAllocationRole(payment: Pick<Payment, 'paymentType'>): PaasAllocationRole {
  if (paasIsFeePayment(payment)) return 'fee'
  return PAAS_ADVANCE_PAYMENT_TYPES.some((type) => type === payment.paymentType) ? 'advance' : 'repayment'
}

export type PaasAllocation = {
  id: number
  payment: Payment
  allocated: Money
}

export type PaasTrader = {
  id: number | null
  organizationId: number
  organizationName: string
}

type PaasPositionCore = {
  supplierPayment: Payment
  advances: PaasAllocation[]
  repayments: PaasAllocation[]
  fees: PaasAllocation[]
  expectedFee: Money | null
  trader: PaasTrader | null
}

type PaasPositionRollup = {
  id: number
  idDisplay: string
  status: PaasStatus
  currency: CurrencyCode
  traderOrganizationName: string | null
  supplierOrganizationName: string
  payableAmount: Money
  financedAmount: Money
  committedAmount: Money
  financedInTransit: Money
  unfinancedAmount: Money
  isFullyFinanced: boolean
  repaidAmount: Money
  repaidInTransit: Money
  outstandingAmount: Money
  overRepaidAmount: Money
  hasFeeTerms: boolean
  feesCollected: Money
  feesRemaining: Money | null
  isFeesCollected: boolean
  totalOutstanding: Money
  isFullyRepaid: boolean
  hasOutstandingBalance: boolean
  repaidCommitted: Money
  feesCommitted: Money
  collectableAmount: Money
  collectableFee: Money | null
  hasCollectableBalance: boolean
  reversedAmount: Money
  needsReconciliation: boolean
  isDraft: boolean
  payableDate: KlearDateJson | undefined
  repaidDate: KlearDateJson | undefined
}

export type PaasPositionJson = PaasPositionCore & PaasPositionRollup

export class PaasPosition {
  static readonly __klearClass = 'PaasPosition'

  readonly supplierPayment: Payment
  readonly advances: PaasAllocation[]
  readonly repayments: PaasAllocation[]
  readonly fees: PaasAllocation[]
  readonly expectedFee: Money | null
  readonly trader: PaasTrader | null

  constructor(props: Pick<PaasPositionJson, 'supplierPayment'> & Partial<PaasPositionCore>) {
    this.supplierPayment = props.supplierPayment
    this.advances = [...(props.advances ?? [])]
    this.repayments = [...(props.repayments ?? [])]
    this.fees = [...(props.fees ?? [])]
    this.expectedFee = props.expectedFee ?? null
    this.trader = props.trader ?? null
  }

  get id(): number {
    return this.supplierPayment.id
  }

  get idDisplay(): string {
    return this.supplierPayment.idDisplay
  }

  get currency(): CurrencyCode {
    return PAAS_SETTLEMENT_CURRENCY
  }

  get traderOrganizationName(): string | null {
    return this.trader?.organizationName ?? this.supplierPayment.sourceAccountOrganizationName ?? null
  }

  get supplierOrganizationName(): string {
    return this.supplierPayment.destinationAccountOrganizationName
  }

  get payableAmount(): Money {
    return this.supplierPayment.amount
  }

  get payableDate(): KlearDate | undefined {
    return this.supplierPayment.effectiveDate
  }

  get financedAmount(): Money {
    return this.sum(this.advances.filter((allocation) => paasPaymentIsSettled(allocation.payment)))
  }

  get financedInTransit(): Money {
    return this.sum(this.advances.filter((allocation) => paasPaymentIsInTransit(allocation.payment)))
  }

  get committedAmount(): Money {
    return this.sum(this.advances.filter((allocation) => !paasPaymentIsVoided(allocation.payment)))
  }

  get unfinancedAmount(): Money {
    if (this.payableAmount.currency !== this.currency) return Money.zero(this.currency)
    return Money.max(this.payableAmount.subtract(this.committedAmount), Money.zero(this.currency))
  }

  get isFullyFinanced(): boolean {
    return !this.unfinancedAmount.isPositive()
  }

  get repaidAmount(): Money {
    return this.sum(this.repayments.filter((allocation) => paasPaymentIsSettled(allocation.payment)))
  }

  get repaidInTransit(): Money {
    return this.sum(this.repayments.filter((allocation) => paasPaymentIsInTransit(allocation.payment)))
  }

  get outstandingAmount(): Money {
    return Money.max(this.financedAmount.subtract(this.repaidAmount), Money.zero(this.currency))
  }

  get repaidCommitted(): Money {
    return this.sum(this.repayments.filter((allocation) => !paasPaymentIsVoided(allocation.payment)))
  }

  get feesCommitted(): Money {
    return this.sum(this.fees.filter((allocation) => !paasPaymentIsVoided(allocation.payment)))
  }

  get collectableAmount(): Money {
    return Money.max(this.financedAmount.subtract(this.repaidCommitted), Money.zero(this.currency))
  }

  get collectableFee(): Money | null {
    if (this.expectedFee == null) return null
    return Money.max(this.expectedFee.subtract(this.feesCommitted), Money.zero(this.currency))
  }

  get hasCollectableBalance(): boolean {
    const fee = this.collectableFee
    return this.collectableAmount.isPositive() || (fee != null && fee.isPositive())
  }

  get overRepaidAmount(): Money {
    return Money.max(this.repaidAmount.subtract(this.financedAmount), Money.zero(this.currency))
  }

  get hasFeeTerms(): boolean {
    return this.expectedFee != null
  }

  get feesCollected(): Money {
    return this.sum(this.fees.filter((allocation) => paasPaymentIsSettled(allocation.payment)))
  }

  get feesRemaining(): Money | null {
    if (this.expectedFee == null) return null
    return Money.max(this.expectedFee.subtract(this.feesCollected), Money.zero(this.currency))
  }

  get isFeesCollected(): boolean {
    const remaining = this.feesRemaining
    return remaining == null || !remaining.isPositive()
  }

  get totalOutstanding(): Money {
    const fees = this.feesRemaining
    return fees == null ? this.outstandingAmount : this.outstandingAmount.add(fees)
  }

  get isFullyRepaid(): boolean {
    if (this.needsReconciliation) return false
    return this.financedAmount.isPositive() && !this.outstandingAmount.isPositive() && this.isFeesCollected
  }

  get hasOutstandingBalance(): boolean {
    if (this.needsReconciliation) return true
    return this.financedAmount.isPositive() && !this.isFullyRepaid
  }

  get liveAdvances(): PaasAllocation[] {
    return this.advances.filter((allocation) => !paasPaymentIsVoided(allocation.payment))
  }

  get reversedAdvances(): PaasAllocation[] {
    return this.advances.filter((allocation) => paasPaymentIsReversal(allocation.payment))
  }

  get reversedAmount(): Money {
    return this.sum(this.reversedAdvances)
  }

  get needsReconciliation(): boolean {
    return this.overRepaidAmount.isPositive()
  }

  get isDraft(): boolean {
    const live = this.liveAdvances
    return live.length > 0 && live.every((allocation) => paymentIsDraft(allocation.payment.status))
  }

  get status(): PaasStatus {
    if (this.needsReconciliation) return PAAS_STATUS_RECONCILE
    if (this.isDraft) return PAAS_STATUS_DRAFT
    if (!this.financedAmount.isPositive()) {
      const allVoided = this.advances.length > 0 && this.liveAdvances.length === 0
      return allVoided ? PAAS_STATUS_VOIDED : PAAS_STATUS_PENDING
    }
    if (this.isFullyRepaid) return PAAS_STATUS_COLLECTED
    if (this.repaidAmount.isPositive() || this.feesCollected.isPositive()) return PAAS_STATUS_PARTIALLY_COLLECTED
    return PAAS_STATUS_OUTSTANDING
  }

  get repaidDate(): KlearDate | undefined {
    if (!this.isFullyRepaid) return undefined
    const dates = [...this.repayments, ...this.fees]
      .filter((allocation) => paasPaymentIsSettled(allocation.payment))
      .map((allocation) => allocation.payment.effectiveDate)
      .filter((date): date is KlearDate => date != null)
    if (dates.length === 0) return undefined
    return dates.reduce((latest, date) => KlearDate.max(latest, date))
  }

  get allocations(): PaasAllocation[] {
    return [...this.advances, ...this.repayments, ...this.fees]
  }

  get foreignCurrencyAllocations(): PaasAllocation[] {
    return this.allocations.filter((allocation) => allocation.allocated.currency !== this.currency)
  }

  private sum(allocations: PaasAllocation[]): Money {
    const amounts = allocations
      .map((allocation) => allocation.allocated)
      .filter((amount) => amount.currency === this.currency)
    if (amounts.length === 0) return Money.zero(this.currency)
    return Money.sum(amounts)
  }

  toJSON(): PaasPositionJson {
    return {
      supplierPayment: this.supplierPayment,
      advances: this.advances,
      repayments: this.repayments,
      fees: this.fees,
      expectedFee: this.expectedFee,
      trader: this.trader,
      id: this.id,
      idDisplay: this.idDisplay,
      status: this.status,
      currency: this.currency,
      traderOrganizationName: this.traderOrganizationName,
      supplierOrganizationName: this.supplierOrganizationName,
      payableAmount: this.payableAmount,
      financedAmount: this.financedAmount,
      committedAmount: this.committedAmount,
      financedInTransit: this.financedInTransit,
      unfinancedAmount: this.unfinancedAmount,
      isFullyFinanced: this.isFullyFinanced,
      repaidAmount: this.repaidAmount,
      repaidInTransit: this.repaidInTransit,
      outstandingAmount: this.outstandingAmount,
      overRepaidAmount: this.overRepaidAmount,
      hasFeeTerms: this.hasFeeTerms,
      feesCollected: this.feesCollected,
      feesRemaining: this.feesRemaining,
      isFeesCollected: this.isFeesCollected,
      totalOutstanding: this.totalOutstanding,
      isFullyRepaid: this.isFullyRepaid,
      hasOutstandingBalance: this.hasOutstandingBalance,
      repaidCommitted: this.repaidCommitted,
      feesCommitted: this.feesCommitted,
      collectableAmount: this.collectableAmount,
      collectableFee: this.collectableFee,
      hasCollectableBalance: this.hasCollectableBalance,
      reversedAmount: this.reversedAmount,
      needsReconciliation: this.needsReconciliation,
      isDraft: this.isDraft,
      payableDate: this.payableDate?.toJSON(),
      repaidDate: this.repaidDate?.toJSON()
    }
  }
}
